import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { date, dateTime, humanise, mobile as fmtMobile } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Button, Chips, Field, Modal, Notice, StatusBadge, Textarea, useToast } from '../components/ui';

interface VerificationRow {
  id: string;
  status: string;
  certificateNo: string | null;
  issuingAuthority: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  rejectReason: string | null;
  createdAt: string;
  provider: {
    id: string;
    name: string;
    category: { name: string; sector: string };
    user: { id: string; mobile: string };
  };
}

/**
 * v2 §19 — mandatory for Local & Home Services before a provider can go live.
 *
 * The certificate document itself is not surfaced here. It is a sensitive
 * personal document held in private storage under DPDP retention rules, so
 * the queue shows the certificate number and issuing authority; retrieving the
 * scan is a separate, logged action rather than something rendered to anyone
 * who opens this page.
 */
export function PoliceVerificationPage() {
  const { get, set } = useListParams({ status: 'SUBMITTED' });
  const status = get('status');
  const [rejecting, setRejecting] = useState<VerificationRow | null>(null);
  const [reason, setReason] = useState('');
  const toast = useToast();
  const qc = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['police'] });
    qc.invalidateQueries({ queryKey: ['dashboard-badges'] });
  };

  const approve = useMutation({
    mutationFn: (id: string) => api.patch(`/admin/police-verifications/${id}/approve`),
    onSuccess: () => {
      toast('Verification approved — the provider can now go live', 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.patch(`/admin/police-verifications/${id}/reject`, { reason }),
    onSuccess: () => {
      toast('Rejected — the provider has been taken offline', 'success');
      setRejecting(null);
      setReason('');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<VerificationRow>> = [
    {
      key: 'provider',
      header: 'Provider',
      render: (r) => (
        <div>
          <div className="cell-strong">{r.provider.name}</div>
          <div className="cell-muted">{fmtMobile(r.provider.user.mobile)}</div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (r) => (
        <div>
          <div>{r.provider.category.name}</div>
          <div className="cell-muted">{humanise(r.provider.category.sector)}</div>
        </div>
      ),
    },
    {
      key: 'certificate',
      header: 'Certificate',
      render: (r) => (
        <div>
          <div className="cell-mono">{r.certificateNo ?? '—'}</div>
          <div className="cell-muted">{r.issuingAuthority ?? 'authority not stated'}</div>
        </div>
      ),
    },
    { key: 'issued', header: 'Issued', render: (r) => <span className="cell-muted">{date(r.issuedAt)}</span> },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge value={r.status} /> },
    { key: 'when', header: 'Submitted', render: (r) => <span className="cell-muted">{dateTime(r.createdAt)}</span> },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.status === 'SUBMITTED' ? (
          <div className="cell-actions">
            <Button
              size="sm"
              variant="success"
              loading={approve.isPending && approve.variables === r.id}
              onClick={() => approve.mutate(r.id)}
            >
              Approve
            </Button>
            <Button size="sm" onClick={() => setRejecting(r)}>
              Reject
            </Button>
          </div>
        ) : r.rejectReason ? (
          <span className="cell-muted cell-clip" title={r.rejectReason}>
            {r.rejectReason}
          </span>
        ) : null,
    },
  ];

  return (
    <div className="stack">
      <Notice tone="warn" title="Handle as sensitive personal data">
        Certificates are held in private storage under DPDP retention rules and are not rendered here. Verify the
        certificate number against the issuing authority's record rather than relying on the uploaded scan.
      </Notice>

      <DataTable<VerificationRow>
        queryKey={['police', status]}
        path="/admin/police-verifications"
        params={{ status }}
        columns={columns}
        emptyIcon="✅"
        emptyTitle={status === 'SUBMITTED' ? 'Nothing waiting for review' : 'No records with this status'}
        toolbar={
          <Chips
            value={status}
            onChange={(v) => set('status', v)}
            options={[
              { value: 'SUBMITTED', label: 'Awaiting review' },
              { value: 'VERIFIED', label: 'Verified' },
              { value: 'REJECTED', label: 'Rejected' },
              { value: 'EXPIRED', label: 'Expired' },
            ]}
          />
        }
      />

      {rejecting && (
        <Modal
          title="Reject this certificate"
          sub={`${rejecting.provider.name} will be taken offline and cannot appear in Local Services until a verified certificate is on file.`}
          onClose={() => setRejecting(null)}
          footer={
            <>
              <Button onClick={() => setRejecting(null)}>Cancel</Button>
              <Button
                variant="danger"
                loading={reject.isPending}
                disabled={!reason.trim()}
                onClick={() => reject.mutate({ id: rejecting.id, reason: reason.trim() })}
              >
                Reject and take offline
              </Button>
            </>
          }
        >
          <Field label="Reason" hint="Shown to the provider.">
            <Textarea
              value={reason}
              autoFocus
              placeholder="e.g. Certificate number could not be verified with the issuing station."
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        </Modal>
      )}
    </div>
  );
}
